/*
=====================================
Shopify Cart API & 共通ミニカートドロワー (sk-mini-cart)
通常ストアページ (layout/theme.liquid) ・ 新LP (layout/sk-lp-blank.liquid) ・ 旧LP 共通
=====================================

運用ルール
1) 外部由来文字列は innerHTML に入れない（textContent / DOM API を使う）。
2) イベント委譲は closest() を使い、子要素クリックの取りこぼしを防ぐ。
3) UIの一時変更（ローディング等）は元状態を退避して復元する。
4) グローバル公開オブジェクトは初期化完了後に代入する。
5) 詳細エラーは console へ、画面通知は必要最小限の文言にする。

公開API (window.ShopifyCartAPI):
- openMiniCart() / closeMiniCart() / toggleMiniCart()
- addItemToCart(variantId, quantity, properties)
- updateItemQuantity(lineOrId, quantity)
- removeItemFromCart(lineOrId)
- getCartData()
- updateCartDisplay()
*/

class ShopifyCartAPI {
  constructor() {
    this.domain =
      window.shopifyConfig?.routes?.shop_domain || window.location.hostname;
    this.storefrontToken =
      window.shopifyConfig?.storefront_access_token ||
      '88d31187db2b9637949f306a43bc270c';
    this.routes = {
      cart_url: window.routes?.cartUrl || window.shopifyConfig?.routes?.cart_url || '/cart',
      cart_add_url:
        window.routes?.cartAddUrl ||
        window.shopifyConfig?.routes?.cart_add_url ||
        '/cart/add',
      cart_change_url:
        window.routes?.cartChangeUrl ||
        window.shopifyConfig?.routes?.cart_change_url ||
        '/cart/change',
      cart_update_url:
        window.shopifyConfig?.routes?.cart_update_url || '/cart/update',
    };
    this.moneyFormat =
      window.theme?.moneyFormat ||
      window.shopifyConfig?.money_format ||
      '¥{{ amount_no_decimals_with_comma_separator }}';
    this.isMiniCartOpen = false;
    this.lastCart = null;
    this.appliedDiscount = null;
    this.isCalculatingDiscount = false;

    this.init();
  }

  init() {
    this.ensureMiniCartDOM();
    this.setupFloatingButtonVisibility();
    this.bindEvents();
    this.updateCartDisplay();
    this.restoreAppliedDiscount();
    this.updateProductAvailability();
  }

  // ========== 共通ミニカートDOM保証（スニペット未出力の旧LP用フォールバック） ==========

  ensureMiniCartDOM() {
    if (!document.getElementById('sk-mini-cart-styles')) {
      const style = document.createElement('style');
      style.id = 'sk-mini-cart-styles';
      style.textContent = `
        :root {
          --sk-mc-navy: var(--heading-color, #1d2088);
          --sk-mc-red: var(--accent-color, #e6002c);
          --sk-mc-cyan: var(--primary-button-background, #00badb);
          --sk-mc-gold: #f6e24b;
          --sk-mc-text: #222222;
          --sk-mc-muted: var(--text-color, #677279);
          --sk-mc-border: var(--border-color, #e1e3e4);
          --sk-mc-bg: var(--secondary-background, #ffffff);
          --sk-mc-surface: var(--background, #f3f5f6);
          --sk-mc-font: "游ゴシック体", YuGothic, "游ゴシック", "Yu Gothic", "Noto Sans JP", sans-serif;
        }
        body.sk-mc-scroll-locked { overflow: hidden !important; }
        .sk-mc-overlay { position: fixed; inset: 0; z-index: 9998; background: rgba(15,18,46,0.48); backdrop-filter: blur(2px); opacity: 0; visibility: hidden; transition: opacity 250ms ease, visibility 250ms ease; }
        .sk-mc-overlay.is-open { opacity: 1; visibility: visible; }
        .sk-mc-drawer { position: fixed; top: 0; right: 0; bottom: 0; z-index: 9999; width: min(100vw, 420px); height: 100vh; height: 100dvh; background: var(--sk-mc-bg); color: var(--sk-mc-text); font-family: var(--sk-mc-font); box-shadow: -8px 0 32px rgba(0,0,0,0.18); display: flex; flex-direction: column; transform: translateX(100%); visibility: hidden; transition: transform 280ms cubic-bezier(0.22,1,0.36,1), visibility 280ms ease; box-sizing: border-box; text-align: left; }
        .sk-mc-drawer *, .sk-mc-drawer *::before, .sk-mc-drawer *::after { box-sizing: border-box; }
        .sk-mc-drawer.is-open { transform: translateX(0); visibility: visible; }
        .sk-mc-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; background: var(--sk-mc-navy); color: #fff; flex-shrink: 0; }
        .sk-mc-header__title-wrap { display: flex; align-items: center; gap: 10px; margin: 0; font-size: 16px; font-weight: 700; color: #fff; }
        .sk-mc-header__icon { width: 20px; height: 20px; flex-shrink: 0; }
        .sk-mc-header__badge { display: inline-flex; align-items: center; justify-content: center; min-width: 24px; height: 22px; padding: 0 8px; border-radius: 999px; background: var(--sk-mc-red); color: #fff; font-size: 12px; font-weight: 700; }
        .sk-mc-header__close { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; margin: -6px -8px -6px 0; border: none; border-radius: 50%; background: transparent; color: #fff; font-size: 24px; cursor: pointer; }
        .sk-mc-shipping { padding: 12px 20px; background: var(--sk-mc-surface); border-bottom: 1px solid var(--sk-mc-border); flex-shrink: 0; }
        .sk-mc-shipping.is-achieved { background: var(--sk-mc-gold); }
        .sk-mc-shipping__text { margin: 0 0 8px; font-size: 13px; font-weight: 700; color: var(--sk-mc-navy); text-align: center; }
        .sk-mc-shipping.is-achieved .sk-mc-shipping__text { margin-bottom: 0; }
        .sk-mc-shipping__bar-track { width: 100%; height: 6px; background: rgba(29,32,136,0.14); border-radius: 999px; overflow: hidden; }
        .sk-mc-shipping.is-achieved .sk-mc-shipping__bar-track { display: none; }
        .sk-mc-shipping__bar-fill { height: 100%; background: linear-gradient(90deg, var(--sk-mc-navy) 0%, var(--sk-mc-cyan) 100%); border-radius: 999px; transition: width 300ms ease; }
        .sk-mc-body { flex: 1 1 auto; overflow-y: auto; padding: 0; -webkit-overflow-scrolling: touch; }
        .sk-mc-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 56px 24px; min-height: 280px; }
        .sk-mc-empty__title { margin: 0 0 8px; font-size: 16px; font-weight: 700; color: var(--sk-mc-navy); }
        .sk-mc-empty__sub { margin: 0 0 24px; font-size: 13px; color: var(--sk-mc-muted); line-height: 1.6; }
        .sk-mc-list { list-style: none; margin: 0; padding: 0 20px; }
        .sk-mc-item { display: grid; grid-template-columns: 76px 1fr; gap: 14px; padding: 16px 0; border-bottom: 1px solid var(--sk-mc-border); transition: opacity 180ms ease; }
        .sk-mc-item.is-updating { opacity: 0.5; pointer-events: none; }
        .sk-mc-item__media { width: 76px; height: 76px; border-radius: 6px; overflow: hidden; background: var(--sk-mc-surface); border: 1px solid var(--sk-mc-border); display: block; }
        .sk-mc-item__img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .sk-mc-item__info { min-width: 0; display: flex; flex-direction: column; justify-content: space-between; }
        .sk-mc-item__title { margin: 0 0 4px; font-size: 14px; font-weight: 700; line-height: 1.4; color: var(--sk-mc-navy); text-decoration: none; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .sk-mc-item__meta { margin: 0 0 4px; font-size: 12px; color: var(--sk-mc-muted); }
        .sk-mc-item__props { list-style: none; margin: 0 0 6px; padding: 0; font-size: 11px; color: var(--sk-mc-muted); }
        .sk-mc-item__price-row { display: flex; align-items: baseline; gap: 8px; margin: 2px 0 10px; }
        .sk-mc-item__price { font-size: 15px; font-weight: 700; color: var(--sk-mc-text); }
        .sk-mc-item__price--sale { color: var(--sk-mc-red); }
        .sk-mc-item__price-compare { font-size: 12px; color: #999; text-decoration: line-through; }
        .sk-mc-item__controls { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
        .sk-mc-qty { display: inline-flex; align-items: center; border: 1px solid var(--sk-mc-border); border-radius: 5px; background: #fff; overflow: hidden; }
        .sk-mc-qty__btn { width: 32px; height: 30px; border: none; background: transparent; color: var(--sk-mc-navy); font-size: 16px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; padding: 0; }
        .sk-mc-qty__input { width: 38px; height: 30px; border: none; border-left: 1px solid var(--sk-mc-border); border-right: 1px solid var(--sk-mc-border); text-align: center; font-size: 13px; font-weight: 700; color: var(--sk-mc-text); background: #fff; padding: 0; }
        .sk-mc-item__remove { border: none; background: none; color: var(--sk-mc-muted); font-size: 12px; text-decoration: underline; cursor: pointer; padding: 4px 6px; }
        .sk-mc-footer { padding: 16px 20px 20px; background: #fff; border-top: 1px solid var(--sk-mc-border); flex-shrink: 0; }
        .sk-mc-footer[hidden] { display: none !important; }
        .sk-mc-subtotal-row { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 6px; }
        .sk-mc-subtotal-label { font-size: 14px; font-weight: 700; color: var(--sk-mc-text); }
        .sk-mc-subtotal-value { font-size: 20px; font-weight: 700; color: var(--sk-mc-navy); }
        .sk-mc-footer__note { margin: 0 0 14px; font-size: 11px; color: var(--sk-mc-muted); }
        .sk-mc-footer__actions { display: flex; flex-direction: column; gap: 8px; }
        .sk-mc-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; width: 100%; min-height: 46px; padding: 12px 16px; border-radius: 6px; font-size: 15px; font-weight: 700; text-decoration: none; text-align: center; cursor: pointer; border: none; }
        .sk-mc-btn--primary { background: var(--sk-mc-cyan); color: #fff !important; }
        .sk-mc-btn--checkout { background: var(--sk-mc-navy); color: #fff !important; }
        .sk-mc-btn--continue { min-height: 40px; padding: 8px 12px; background: transparent; color: var(--sk-mc-muted); border: 1px solid var(--sk-mc-border); font-size: 13px; }
        .sk-mc-floating-btn { position: fixed; right: 18px; bottom: 20px; z-index: 9990; display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 52px; padding: 0 18px; border-radius: 999px; background: var(--sk-mc-navy); color: #fff; border: 2px solid #fff; box-shadow: 0 6px 20px rgba(29,32,136,0.32); font-family: var(--sk-mc-font); font-size: 14px; font-weight: 700; cursor: pointer; }
        .sk-mc-floating-btn[hidden] { display: none !important; }
        .sk-mc-floating-btn__badge { display: inline-flex; align-items: center; justify-content: center; min-width: 22px; height: 22px; padding: 0 6px; border-radius: 999px; background: var(--sk-mc-red); color: #fff; font-size: 12px; font-weight: 700; }
        .notification { position: fixed; z-index: 10001; right: 16px; bottom: 16px; display: flex; align-items: center; gap: 10px; max-width: min(92vw, 380px); padding: 14px 16px; border-radius: 8px; background: #1a1a1a; color: #fff; font-family: var(--sk-mc-font); font-size: 14px; line-height: 1.4; box-shadow: 0 8px 24px rgba(0,0,0,0.24); opacity: 0; transform: translateY(12px); transition: opacity 200ms ease, transform 200ms ease; pointer-events: none; }
        .notification.show { opacity: 1; transform: translateY(0); pointer-events: auto; }
        .notification.error { background: #b3261e; }
        .notification-icon { flex-shrink: 0; font-weight: 700; }
        .notification-message { flex: 1; }
        .notification-action { flex-shrink: 0; border: 1px solid rgba(255,255,255,0.4); border-radius: 4px; background: rgba(255,255,255,0.12); color: #fff; font-size: 12px; font-weight: 700; padding: 5px 10px; cursor: pointer; }
        .notification-close { flex-shrink: 0; border: 0; background: none; color: inherit; font-size: 18px; line-height: 1; cursor: pointer; padding: 0 2px; }
      `;
      document.head.appendChild(style);
    }

    if (!document.getElementById('sk-mini-cart')) {
      const overlay = document.createElement('div');
      overlay.id = 'sk-mini-cart-overlay';
      overlay.className = 'sk-mc-overlay';
      overlay.setAttribute('data-sk-mc-close', '');
      overlay.setAttribute('aria-hidden', 'true');

      const drawer = document.createElement('aside');
      drawer.id = 'sk-mini-cart';
      drawer.className = 'sk-mc-drawer';
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      drawer.setAttribute('aria-label', 'ショッピングカート');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.dataset.skCartBehavior = 'drawer';
      drawer.dataset.skFreeShippingThreshold = '10000';
      drawer.dataset.skShowCheckout = 'false';
      drawer.dataset.skCartUrl = this.routes.cart_url || '/cart';
      drawer.dataset.skFloatingMode = 'auto';

      drawer.innerHTML = `
        <header class="sk-mc-header">
          <h2 class="sk-mc-header__title-wrap">
            <span>🛒 カート</span>
            <span class="sk-mc-header__badge" data-sk-mc-count>0</span>
          </h2>
          <button type="button" class="sk-mc-header__close" data-sk-mc-close aria-label="カートを閉じる">&times;</button>
        </header>
        <div class="sk-mc-shipping" data-sk-mc-shipping>
          <p class="sk-mc-shipping__text" data-sk-mc-shipping-text>10,000円以上のご購入で送料無料！</p>
          <div class="sk-mc-shipping__bar-track">
            <div class="sk-mc-shipping__bar-fill" data-sk-mc-shipping-bar style="width: 0%;"></div>
          </div>
        </div>
        <div class="sk-mc-body" data-sk-mc-body></div>
        <footer class="sk-mc-footer" data-sk-mc-footer hidden>
          <div class="sk-mc-subtotal-row">
            <span class="sk-mc-subtotal-label">小計（税込）</span>
            <span class="sk-mc-subtotal-value" data-sk-mc-total>¥0</span>
          </div>
          <p class="sk-mc-footer__note">※送料・ギフト設定・同意確認はカート画面で行えます。</p>
          <div class="sk-mc-footer__actions">
            <a href="${this.routes.cart_url || '/cart'}" class="sk-mc-btn sk-mc-btn--primary">カートを見る・ご購入手続きへ</a>
            <button type="button" class="sk-mc-btn sk-mc-btn--continue" data-sk-mc-close>お買い物を続ける</button>
          </div>
        </footer>
      `;

      const floatingBtn = document.createElement('button');
      floatingBtn.type = 'button';
      floatingBtn.id = 'sk-floating-cart-btn';
      floatingBtn.className = 'sk-mc-floating-btn';
      floatingBtn.setAttribute('data-sk-mini-cart-toggle', '');
      floatingBtn.setAttribute('aria-controls', 'sk-mini-cart');
      floatingBtn.setAttribute('aria-expanded', 'false');
      floatingBtn.setAttribute('aria-label', 'カートを見る');
      floatingBtn.hidden = true;
      floatingBtn.innerHTML = `
        <span>🛒 カート</span>
        <span class="sk-mc-floating-btn__badge cart-count" data-cart-count>0</span>
      `;

      document.body.appendChild(overlay);
      document.body.appendChild(drawer);
      document.body.appendChild(floatingBtn);
    }
  }

  setupFloatingButtonVisibility() {
    const drawer = document.getElementById('sk-mini-cart');
    const floatingBtn = document.getElementById('sk-floating-cart-btn');
    if (!floatingBtn || !drawer) return;

    const mode = drawer.dataset.skFloatingMode || 'auto';
    if (mode === 'never') {
      floatingBtn.hidden = true;
      return;
    }
    if (mode === 'always') {
      floatingBtn.hidden = false;
      return;
    }

    // auto: ページ内にヘッダーカートボタンが無い場合のみフローティングボタンを表示
    const hasHeaderCart = Boolean(
      document.querySelector(
        '.header__cart-toggle, .sk-lp-header__cart, #cartButton, [data-cart-icon]:not(#sk-floating-cart-btn)'
      )
    );
    floatingBtn.hidden = hasHeaderCart;
  }

  // ========== 設定解決ヘルパー ==========

  resolveCartBehavior(triggerEl = null) {
    if (triggerEl) {
      const explicitEl = triggerEl.closest('[data-cart-behavior], [data-sk-cart-behavior]');
      if (explicitEl) {
        return (
          explicitEl.dataset.cartBehavior ||
          explicitEl.dataset.skCartBehavior ||
          'drawer'
        );
      }
    }

    const sectionBehaviorEl = document.querySelector(
      '[data-sk-cart-behavior]:not(#sk-mini-cart)'
    );
    if (sectionBehaviorEl && sectionBehaviorEl.dataset.skCartBehavior) {
      return sectionBehaviorEl.dataset.skCartBehavior;
    }

    const drawer = document.getElementById('sk-mini-cart');
    if (drawer && drawer.dataset.skCartBehavior) {
      return drawer.dataset.skCartBehavior;
    }

    return 'drawer';
  }

  resolveFreeShippingThreshold() {
    const customEl = document.querySelector(
      '[data-sk-free-shipping-threshold]:not(#sk-mini-cart)'
    );
    if (customEl && customEl.dataset.skFreeShippingThreshold !== undefined) {
      const val = Number(customEl.dataset.skFreeShippingThreshold);
      if (!Number.isNaN(val)) return val;
    }

    const headerBar = document.querySelector('.custom-free-shipping-bar[data-threshold]');
    if (headerBar && headerBar.dataset.threshold) {
      const cents = Number(headerBar.dataset.threshold);
      if (!Number.isNaN(cents) && cents > 0) return Math.round(cents / 100);
    }

    const drawer = document.getElementById('sk-mini-cart');
    if (drawer && drawer.dataset.skFreeShippingThreshold !== undefined) {
      const val = Number(drawer.dataset.skFreeShippingThreshold);
      if (!Number.isNaN(val)) return val;
    }

    return 10000;
  }

  // ========== ミニカート開閉メソッド ==========

  openMiniCart() {
    if (window.theme?.pageType === 'cart') return;

    this.ensureMiniCartDOM();
    const drawer = document.getElementById('sk-mini-cart');
    const overlay = document.getElementById('sk-mini-cart-overlay');
    if (!drawer || !overlay) return;

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sk-mc-scroll-locked');
    this.isMiniCartOpen = true;

    document
      .querySelectorAll('[data-sk-mini-cart-toggle], .header__cart-toggle, .sk-lp-header__cart')
      .forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
  }

  closeMiniCart() {
    const drawer = document.getElementById('sk-mini-cart');
    const overlay = document.getElementById('sk-mini-cart-overlay');
    if (!drawer || !overlay) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sk-mc-scroll-locked');
    this.isMiniCartOpen = false;

    document
      .querySelectorAll('[data-sk-mini-cart-toggle], .header__cart-toggle, .sk-lp-header__cart')
      .forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
  }

  toggleMiniCart() {
    if (this.isMiniCartOpen) {
      this.closeMiniCart();
    } else {
      this.openMiniCart();
      this.updateCartDisplay();
    }
  }

  // ========== GraphQL Storefront API ==========

  async graphqlQuery(query, variables = {}) {
    if (!this.storefrontToken) {
      console.warn('Storefront Access Tokenが設定されていません');
      return null;
    }

    try {
      const response = await fetch(`/api/2024-04/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      const data = await response.json();

      if (data.errors) {
        console.error('GraphQL Errors:', data.errors);
        return null;
      }

      return data.data;
    } catch (error) {
      console.error('GraphQL Request Error:', error);
      return null;
    }
  }

  // ========== クーポンコード・ディスカウント管理 (Storefront Cart API) ==========

  async validateAndApplyDiscount(rawCode, silent = false) {
    if (!rawCode || !rawCode.trim()) {
      return { success: false, message: 'クーポンコードを入力してください' };
    }
    const code = rawCode.trim();

    if (!this.storefrontToken) {
      return { success: false, message: 'Storefront APIが設定されていません' };
    }

    const cart = this.lastCart || (await this.getCart());
    if (!cart || !cart.items || cart.items.length === 0) {
      return { success: false, message: 'カートに商品がありません' };
    }

    this.isCalculatingDiscount = true;
    this.setDiscountButtonLoading(true);

    try {
      const lines = cart.items.map((item) => ({
        merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
        quantity: Number(item.quantity) || 1,
      }));

      const query = `
        mutation CreateCartWithDiscount($lines: [CartLineInput!]!, $discountCodes: [String!]) {
          cartCreate(input: {
            lines: $lines,
            discountCodes: $discountCodes
          }) {
            cart {
              id
              checkoutUrl
              cost {
                subtotalAmount { amount currencyCode }
                totalAmount { amount currencyCode }
              }
              discountCodes {
                code
                applicable
              }
              discountAllocations {
                discountedAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 50) {
                edges {
                  node {
                    id
                    cost {
                      totalAmount { amount currencyCode }
                    }
                    discountAllocations {
                      discountedAmount {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
            userErrors {
              field
              message
              code
            }
          }
        }
      `;

      const result = await this.graphqlQuery(query, {
        lines,
        discountCodes: [code],
      });

      if (!result || !result.cartCreate) {
        throw new Error('クーポン検証APIの呼び出しに失敗しました');
      }

      const { cart: sfCart, userErrors } = result.cartCreate;

      if (userErrors && userErrors.length > 0) {
        // バリアント/商品がStorefront API販売チャネルに未公開の場合のエラー判定
        const isMerchandiseError = userErrors.some(
          (err) =>
            err.field?.includes('merchandiseId') ||
            err.field?.includes('lines') ||
            (err.message &&
              (err.message.includes('ProductVariant') ||
                err.message.includes('商品は存在しません') ||
                err.message.includes('does not exist')))
        );

        if (isMerchandiseError) {
          // Storefront API側で商品が未公開でも、Shopify標準チェックアウト画面ではクーポンが適用可能
          this.appliedDiscount = {
            code: code,
            discountAmount: 0,
            discountAmountFormatted: 'レジで自動反映',
            totalAmount: (cart.total_price || 0) / 100,
            totalAmountFormatted: this.formatMoney(cart.total_price || 0),
            checkoutUrl: `/checkout?discount=${encodeURIComponent(code)}`,
            isPendingCheckout: true,
          };

          try {
            sessionStorage.setItem('sk_applied_discount_code', code);
          } catch (e) {}

          this.updateDiscountUI();
          if (!silent) {
            this.showDiscountMessage(
              `クーポン「${code}」を保持しました（割引額はご購入手続き画面で自動反映されます）`,
              'success'
            );
            this.showNotification(`クーポン「${code}」をセットしました！`, 'success');
          }

          return { success: true, discount: this.appliedDiscount, pendingCheckout: true };
        }

        let errorMsg = userErrors[0].message || 'クーポンを適用できませんでした';
        if (errorMsg.includes('ProductVariant') || errorMsg.includes('gid://')) {
          errorMsg = '入力されたクーポンコードは無効か、適用条件を満たしていません';
        }
        if (!silent) this.showDiscountMessage(errorMsg, 'error');
        return { success: false, message: errorMsg };
      }

      // クーポンコードの適用判定
      const appliedCodeObj = sfCart?.discountCodes?.find(
        (dc) => dc.code.toLowerCase() === code.toLowerCase()
      );

      if (!appliedCodeObj || !appliedCodeObj.applicable) {
        const invalidMsg = '入力されたクーポンコードは無効か、適用条件を満たしていません';
        if (!silent) this.showDiscountMessage(invalidMsg, 'error');
        return { success: false, message: invalidMsg };
      }

      // 金額計算
      const subtotal = parseFloat(sfCart.cost?.subtotalAmount?.amount || '0');
      const total = parseFloat(sfCart.cost?.totalAmount?.amount || '0');
      let discountYen = Math.max(0, subtotal - total);

      // discountAllocations からも合算確認（商品単位割引の場合の安全策）
      if (discountYen === 0 && sfCart.lines?.edges) {
        sfCart.lines.edges.forEach(({ node }) => {
          if (node.discountAllocations) {
            node.discountAllocations.forEach((alloc) => {
              discountYen += parseFloat(alloc.discountedAmount?.amount || '0');
            });
          }
        });
      }

      this.appliedDiscount = {
        code: appliedCodeObj.code,
        discountAmount: discountYen,
        discountAmountFormatted: `-¥${Math.round(discountYen).toLocaleString('ja-JP')}`,
        totalAmount: total,
        totalAmountFormatted: `¥${Math.round(total).toLocaleString('ja-JP')}`,
        checkoutUrl: `/checkout?discount=${encodeURIComponent(appliedCodeObj.code)}`,
      };

      try {
        sessionStorage.setItem('sk_applied_discount_code', appliedCodeObj.code);
      } catch (e) {}

      this.updateDiscountUI();
      if (!silent) {
        this.showDiscountMessage(`クーポン「${appliedCodeObj.code}」を適用しました`, 'success');
        this.showNotification(`クーポン「${appliedCodeObj.code}」が適用されました！`, 'success');
      }

      return { success: true, discount: this.appliedDiscount };
    } catch (error) {
      console.error('Validate discount error:', error);
      // 通信エラーやStorefront APIエラー時もチェックアウト画面への引き継ぎフォールバックを提供
      const cart = this.lastCart || {};
      this.appliedDiscount = {
        code: code,
        discountAmount: 0,
        discountAmountFormatted: 'レジで自動反映',
        totalAmount: (cart.total_price || 0) / 100,
        totalAmountFormatted: this.formatMoney(cart.total_price || 0),
        checkoutUrl: `/checkout?discount=${encodeURIComponent(code)}`,
        isPendingCheckout: true,
      };

      try {
        sessionStorage.setItem('sk_applied_discount_code', code);
      } catch (e) {}

      this.updateDiscountUI();
      if (!silent) {
        this.showDiscountMessage(
          `クーポン「${code}」を保持しました（割引額はご購入手続き画面で自動反映されます）`,
          'success'
        );
        this.showNotification(`クーポン「${code}」をセットしました！`, 'success');
      }

      return { success: true, discount: this.appliedDiscount, pendingCheckout: true };
    } finally {
      this.isCalculatingDiscount = false;
      this.setDiscountButtonLoading(false);
    }
  }

  removeDiscount() {
    const prevCode = this.appliedDiscount?.code;
    this.appliedDiscount = null;
    try {
      sessionStorage.removeItem('sk_applied_discount_code');
    } catch (e) {}

    this.updateDiscountUI();
    if (prevCode) {
      this.showNotification(`クーポン「${prevCode}」を解除しました`, 'info');
    }
  }

  async restoreAppliedDiscount() {
    let savedCode = null;
    try {
      savedCode = sessionStorage.getItem('sk_applied_discount_code');
    } catch (e) {}

    if (!savedCode) return;

    const cart = this.lastCart || (await this.getCart());
    if (cart && cart.item_count > 0) {
      await this.validateAndApplyDiscount(savedCode, true);
    } else {
      this.removeDiscount();
    }
  }

  updateDiscountUI() {
    const isApplied = Boolean(this.appliedDiscount);
    const containers = document.querySelectorAll('[data-sk-discount-container]');

    containers.forEach((container) => {
      const toggleWrap = container.querySelector('[data-sk-discount-toggle-wrap]');
      const panel = container.querySelector('[data-sk-discount-panel]');
      const appliedBox = container.querySelector('[data-sk-discount-applied]');
      const codeEl = container.querySelector('[data-sk-discount-code]');
      const msgEl = container.querySelector('[data-sk-discount-msg]');

      if (isApplied) {
        if (toggleWrap) toggleWrap.hidden = true;
        if (panel) panel.hidden = true;
        if (appliedBox) appliedBox.hidden = false;
        if (codeEl) codeEl.textContent = this.appliedDiscount.code;
        if (msgEl) {
          msgEl.hidden = true;
          msgEl.textContent = '';
        }
      } else {
        if (toggleWrap) toggleWrap.hidden = false;
        if (appliedBox) appliedBox.hidden = true;
      }
    });

    // 割引額行 & 割引後合計行の更新
    const discountRows = document.querySelectorAll('[data-sk-mc-discount-row]');
    const discountedTotalRows = document.querySelectorAll('[data-sk-mc-discounted-total-row]');
    const originalTotals = document.querySelectorAll(
      '[data-sk-mc-total], [data-cart-main-total]'
    );

    if (isApplied) {
      const isPending = Boolean(this.appliedDiscount.isPendingCheckout);

      discountRows.forEach((row) => {
        row.hidden = false;
        const labelCode = row.querySelector('[data-sk-mc-discount-label-code]');
        const amountEl = row.querySelector('[data-sk-mc-discount-amount]');
        if (labelCode) labelCode.textContent = this.appliedDiscount.code;
        if (amountEl) {
          amountEl.textContent = isPending ? 'レジで自動反映' : this.appliedDiscount.discountAmountFormatted;
        }
      });

      discountedTotalRows.forEach((row) => {
        if (isPending) {
          row.hidden = true;
        } else {
          row.hidden = false;
          const totalEl = row.querySelector('[data-sk-mc-discounted-total]');
          if (totalEl) totalEl.textContent = this.appliedDiscount.totalAmountFormatted;
        }
      });

      originalTotals.forEach((el) => {
        if (isPending) {
          el.classList.remove('is-strikethrough');
        } else {
          el.classList.add('is-strikethrough');
        }
      });
    } else {
      discountRows.forEach((row) => {
        row.hidden = true;
      });
      discountedTotalRows.forEach((row) => {
        row.hidden = true;
      });
      originalTotals.forEach((el) => {
        el.classList.remove('is-strikethrough');
      });
    }

    // チェックアウトリンク／ボタンのURL同期
    this.syncCheckoutButtons();
  }

  syncCheckoutButtons() {
    const isApplied = Boolean(this.appliedDiscount);
    const code = this.appliedDiscount?.code;

    // ミニカート内のチェックアウトボタン
    const checkoutDirectLinks = document.querySelectorAll('[data-sk-mc-checkout-direct]');
    checkoutDirectLinks.forEach((link) => {
      link.href = isApplied ? `/checkout?discount=${encodeURIComponent(code)}` : '/checkout';
    });

    const checkoutLinks = document.querySelectorAll('[data-sk-mc-checkout-link]');
    checkoutLinks.forEach((link) => {
      link.href = isApplied
        ? `${this.routes.cart_url || '/cart'}?discount=${encodeURIComponent(code)}`
        : (this.routes.cart_url || '/cart');
    });

    // メインカート画面のフォーム
    const mainCartForm = document.querySelector('form.cart-recap');
    if (mainCartForm) {
      let discountHiddenInput = mainCartForm.querySelector('input[name="discount"]');
      if (isApplied) {
        if (!discountHiddenInput) {
          discountHiddenInput = document.createElement('input');
          discountHiddenInput.type = 'hidden';
          discountHiddenInput.name = 'discount';
          mainCartForm.appendChild(discountHiddenInput);
        }
        discountHiddenInput.value = code;
      } else if (discountHiddenInput) {
        discountHiddenInput.remove();
      }
    }
  }

  showDiscountMessage(text, type = 'info') {
    const msgEls = document.querySelectorAll('[data-sk-discount-msg]');
    msgEls.forEach((el) => {
      el.textContent = text;
      el.className = `sk-discount-box__msg is-${type}`;
      el.hidden = false;
    });
  }

  setDiscountButtonLoading(isLoading) {
    const btns = document.querySelectorAll('[data-sk-discount-apply]');
    btns.forEach((btn) => {
      btn.disabled = isLoading;
      if (isLoading) {
        btn.dataset.prevText = btn.textContent;
        btn.textContent = '確認中...';
      } else if (btn.dataset.prevText) {
        btn.textContent = btn.dataset.prevText;
      }
    });
  }

  async getProductByHandle(handle) {
    const query = `
      query getProduct($handle: String!) {
        product(handle: $handle) {
          id
          handle
          title
          description
          availableForSale
          totalInventory
          productType
          tags
          priceRange {
            minVariantPrice { amount currencyCode }
            maxVariantPrice { amount currencyCode }
          }
          variants(first: 100) {
            edges {
              node {
                id
                title
                availableForSale
                quantityAvailable
                currentlyNotInStock
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
                selectedOptions { name value }
              }
            }
          }
          images(first: 10) {
            edges {
              node {
                id
                url(transform: {maxWidth: 800, maxHeight: 800})
                altText
                width
                height
              }
            }
          }
        }
      }
    `;

    const result = await this.graphqlQuery(query, { handle });
    return result?.product || null;
  }

  // ========== Ajax Cart API ==========

  async addToCart(items) {
    try {
      const url = (this.routes.cart_add_url || '/cart/add').replace(/\.js$/, '') + '.js';
      const payload = Array.isArray(items)
        ? { items }
        : items && items.items
        ? items
        : { items: [items] };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || data.description || 'カートへの追加に失敗しました'
        );
      }

      await this.updateCartDisplay();
      return data;
    } catch (error) {
      console.error('Add to cart error:', error);
      throw error;
    }
  }

  async addFormToCart(formElement) {
    try {
      const url = (this.routes.cart_add_url || '/cart/add').replace(/\.js$/, '') + '.js';
      const formData = new FormData(formElement);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || data.description || 'カートへの追加に失敗しました'
        );
      }

      await this.updateCartDisplay();
      return data;
    } catch (error) {
      console.error('Add form to cart error:', error);
      throw error;
    }
  }

  async updateCart(updates) {
    try {
      const url =
        (this.routes.cart_update_url || '/cart/update').replace(/\.js$/, '') + '.js';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        throw new Error('カートの更新に失敗しました');
      }

      const data = await response.json();
      await this.updateCartDisplay(data);
      return data;
    } catch (error) {
      console.error('Update cart error:', error);
      throw error;
    }
  }

  async changeCartItem(lineOrId, quantity) {
    try {
      const url =
        (this.routes.cart_change_url || '/cart/change').replace(/\.js$/, '') + '.js';
      const isLineIndex =
        typeof lineOrId === 'number' ||
        (/^\d+$/.test(String(lineOrId)) && Number(lineOrId) < 1000);

      const payload = isLineIndex
        ? { line: Number(lineOrId), quantity: Number(quantity) }
        : { id: String(lineOrId), quantity: Number(quantity) };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || data.description || '商品数量の変更に失敗しました'
        );
      }

      await this.updateCartDisplay(data);
      return data;
    } catch (error) {
      console.error('Change cart item error:', error);
      throw error;
    }
  }

  async getCart() {
    try {
      const response = await fetch(`/cart.js?_=${Date.now()}`, {
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error('カート情報の取得に失敗しました');
      }

      return await response.json();
    } catch (error) {
      console.error('Get cart error:', error);
      return null;
    }
  }

  async clearCart() {
    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('カートのクリアに失敗しました');
      }

      const data = await response.json();
      await this.updateCartDisplay(data);
      return true;
    } catch (error) {
      console.error('Clear cart error:', error);
      return false;
    }
  }

  // ========== UI更新＆ミニカート再描画 ==========

  async updateCartDisplay(providedCart = null) {
    const cart = providedCart || (await this.getCart());
    if (!cart) return null;

    this.lastCart = cart;

    // 1. 全ページのカート数バッジ更新
    this.updateCartCount(cart.item_count);

    // 2. カート総額表示の更新
    this.updateCartTotal(cart.total_price);

    // 3. カートアイコンの状態更新
    this.updateCartIcon(cart.item_count > 0);

    // 4. 共通ミニカートドロワーの再描画
    this.renderMiniCart(cart);

    // 5. ヘッダー送料無料バー等の外部リスナー通知
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    if (typeof window.refreshFreeShippingBar === 'function') {
      window.refreshFreeShippingBar();
    }

    // 6. クーポン割引の再計算＆UI反映
    if (this.appliedDiscount && !this.isCalculatingDiscount) {
      if (cart.item_count === 0) {
        this.removeDiscount();
      } else {
        this.validateAndApplyDiscount(this.appliedDiscount.code, true);
      }
    } else {
      this.updateDiscountUI();
    }

    return cart;
  }

  updateCartCount(count) {
    if (window.theme) {
      window.theme.cartCount = count;
    }

    const cartCountElements = document.querySelectorAll(
      '.header__cart-count, .cart-count, [data-cart-count], [data-sk-mc-count], #cartCount'
    );
    cartCountElements.forEach((element) => {
      element.textContent = count;
      if (element.id === 'cartCount') {
        element.classList.toggle('hidden', count === 0);
      } else if (
        element.classList.contains('sk-lp-header__cart-badge') ||
        (element.hasAttribute('data-cart-count') &&
          !element.classList.contains('sk-mc-floating-btn__badge'))
      ) {
        element.style.display = count > 0 ? 'inline-flex' : 'none';
      }
    });
  }

  updateCartTotal(totalPrice) {
    const formatted = this.formatMoney(totalPrice);
    const cartTotalElements = document.querySelectorAll(
      '.cart-total, [data-cart-total], [data-sk-mc-total]'
    );
    cartTotalElements.forEach((element) => {
      element.textContent = formatted;
    });
    this.updateDiscountUI();
  }

  updateCartIcon(hasItems) {
    const cartIcons = document.querySelectorAll(
      '.cart-icon, [data-cart-icon], .header__cart-toggle'
    );
    cartIcons.forEach((icon) => {
      icon.classList.toggle('has-items', hasItems);
    });
  }

  renderMiniCart(cart) {
    const drawer = document.getElementById('sk-mini-cart');
    if (!drawer) return;

    // 送料無料カウントダウン更新
    const thresholdYen = this.resolveFreeShippingThreshold();
    const thresholdCents = thresholdYen * 100;
    const shippingBox = drawer.querySelector('[data-sk-mc-shipping]');
    const shippingText = drawer.querySelector('[data-sk-mc-shipping-text]');
    const shippingBar = drawer.querySelector('[data-sk-mc-shipping-bar]');

    if (shippingBox && thresholdYen > 0) {
      shippingBox.hidden = false;
      const totalCents = Number(cart.total_price || 0);
      if (totalCents >= thresholdCents && cart.item_count > 0) {
        shippingBox.classList.add('is-achieved');
        if (shippingText) {
          shippingText.textContent = 'おめでとうございます！送料無料が適用されます';
        }
        if (shippingBar) {
          shippingBar.style.width = '100%';
        }
      } else if (cart.item_count > 0) {
        shippingBox.classList.remove('is-achieved');
        const remainYen = Math.max(
          0,
          Math.round((thresholdCents - totalCents) / 100)
        );
        const pct = Math.min(
          99,
          Math.max(0, Math.round((totalCents / thresholdCents) * 100))
        );
        if (shippingText) {
          shippingText.textContent = `あと ${remainYen.toLocaleString('ja-JP')} 円で送料無料！`;
        }
        if (shippingBar) {
          shippingBar.style.width = `${pct}%`;
        }
      } else {
        shippingBox.classList.remove('is-achieved');
        if (shippingText) {
          shippingText.textContent = `${thresholdYen.toLocaleString('ja-JP')}円以上のご購入で送料無料！`;
        }
        if (shippingBar) {
          shippingBar.style.width = '0%';
        }
      }
    } else if (shippingBox) {
      shippingBox.hidden = true;
    }

    // ボディ（商品リストまたは空状態）の描画
    const bodyEl = drawer.querySelector('[data-sk-mc-body]');
    const footerEl = drawer.querySelector('[data-sk-mc-footer]');
    if (!bodyEl) return;

    const prevScrollTop = bodyEl.scrollTop;
    bodyEl.innerHTML = '';

    if (!cart.items || cart.items.length === 0) {
      if (footerEl) footerEl.hidden = true;

      const emptyDiv = document.createElement('div');
      emptyDiv.className = 'sk-mc-empty';
      emptyDiv.innerHTML = `
        <svg class="sk-mc-empty__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 4h2l1.6 9.6a2 2 0 0 0 2 1.7h8.2a2 2 0 0 0 2-1.6L20.4 8H6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="9.5" cy="20" r="1.4" fill="currentColor"/>
          <circle cx="17.5" cy="20" r="1.4" fill="currentColor"/>
        </svg>
        <p class="sk-mc-empty__title">カートに商品がありません</p>
        <p class="sk-mc-empty__sub">気になる商品をカートに追加すると、こちらで数量変更や合計金額の確認ができます。</p>
        <button type="button" class="sk-mc-btn sk-mc-btn--primary" data-sk-mc-close style="max-width: 240px;">お買い物を続ける</button>
      `;
      bodyEl.appendChild(emptyDiv);
      return;
    }

    if (footerEl) footerEl.hidden = false;

    const ul = document.createElement('ul');
    ul.className = 'sk-mc-list';
    ul.setAttribute('role', 'list');
    ul.setAttribute('data-sk-mc-list', '');

    cart.items.forEach((item, index) => {
      const lineIndex = index + 1;
      const li = document.createElement('li');
      li.className = 'sk-mc-item';
      li.setAttribute('data-sk-mc-line', String(lineIndex));

      // 商品サムネイル
      const mediaLink = document.createElement('a');
      mediaLink.href = item.url || '#';
      mediaLink.className = 'sk-mc-item__media';

      if (item.image || item.featured_image?.url) {
        const img = document.createElement('img');
        img.className = 'sk-mc-item__img';
        img.src = item.image || item.featured_image.url;
        img.alt = item.product_title || item.title || '';
        img.loading = 'lazy';
        mediaLink.appendChild(img);
      }

      // 商品情報カラム
      const infoDiv = document.createElement('div');
      infoDiv.className = 'sk-mc-item__info';

      const topGroup = document.createElement('div');

      const titleLink = document.createElement('a');
      titleLink.href = item.url || '#';
      titleLink.className = 'sk-mc-item__title';
      titleLink.textContent = item.product_title || item.title || '';
      topGroup.appendChild(titleLink);

      if (item.variant_title && item.variant_title !== 'Default Title') {
        const variantP = document.createElement('p');
        variantP.className = 'sk-mc-item__meta';
        variantP.textContent = item.variant_title;
        topGroup.appendChild(variantP);
      }

      if (item.selling_plan_allocation?.selling_plan?.name) {
        const planP = document.createElement('p');
        planP.className = 'sk-mc-item__meta';
        planP.textContent = item.selling_plan_allocation.selling_plan.name;
        topGroup.appendChild(planP);
      }

      if (item.properties && typeof item.properties === 'object') {
        const propEntries = Object.entries(item.properties).filter(
          ([key, val]) => val && String(val).trim() !== '' && !String(key).startsWith('_')
        );
        if (propEntries.length > 0) {
          const propList = document.createElement('ul');
          propList.className = 'sk-mc-item__props';
          propEntries.forEach(([key, val]) => {
            const propLi = document.createElement('li');
            propLi.textContent = `${key}: ${val}`;
            propList.appendChild(propLi);
          });
          topGroup.appendChild(propList);
        }
      }

      const priceRow = document.createElement('div');
      priceRow.className = 'sk-mc-item__price-row';

      const finalPriceSpan = document.createElement('span');
      const hasDiscount =
        typeof item.original_line_price === 'number' &&
        item.original_line_price > item.final_line_price;
      finalPriceSpan.className = hasDiscount
        ? 'sk-mc-item__price sk-mc-item__price--sale'
        : 'sk-mc-item__price';
      finalPriceSpan.textContent = this.formatMoney(item.final_line_price);
      priceRow.appendChild(finalPriceSpan);

      if (hasDiscount) {
        const compareSpan = document.createElement('span');
        compareSpan.className = 'sk-mc-item__price-compare';
        compareSpan.textContent = this.formatMoney(item.original_line_price);
        priceRow.appendChild(compareSpan);
      }

      topGroup.appendChild(priceRow);
      infoDiv.appendChild(topGroup);

      // 数量変更＆削除ボタン
      const controlsDiv = document.createElement('div');
      controlsDiv.className = 'sk-mc-item__controls';

      const qtyWrap = document.createElement('div');
      qtyWrap.className = 'sk-mc-qty';

      const minusBtn = document.createElement('button');
      minusBtn.type = 'button';
      minusBtn.className = 'sk-mc-qty__btn';
      minusBtn.setAttribute('data-sk-mc-qty-btn', '');
      minusBtn.dataset.line = String(lineIndex);
      minusBtn.dataset.quantity = String(Math.max(0, item.quantity - 1));
      minusBtn.setAttribute('aria-label', '数量を減らす');
      minusBtn.textContent = '−';

      const qtyInput = document.createElement('input');
      qtyInput.type = 'number';
      qtyInput.className = 'sk-mc-qty__input';
      qtyInput.setAttribute('data-sk-mc-qty-input', '');
      qtyInput.dataset.line = String(lineIndex);
      qtyInput.value = String(item.quantity);
      qtyInput.min = '0';
      qtyInput.inputMode = 'numeric';
      qtyInput.setAttribute('aria-label', '数量');

      const plusBtn = document.createElement('button');
      plusBtn.type = 'button';
      plusBtn.className = 'sk-mc-qty__btn';
      plusBtn.setAttribute('data-sk-mc-qty-btn', '');
      plusBtn.dataset.line = String(lineIndex);
      plusBtn.dataset.quantity = String(item.quantity + 1);
      plusBtn.setAttribute('aria-label', '数量を増やす');
      plusBtn.textContent = '+';

      qtyWrap.appendChild(minusBtn);
      qtyWrap.appendChild(qtyInput);
      qtyWrap.appendChild(plusBtn);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'sk-mc-item__remove';
      removeBtn.setAttribute('data-sk-mc-qty-btn', '');
      removeBtn.dataset.line = String(lineIndex);
      removeBtn.dataset.quantity = '0';
      removeBtn.textContent = '削除';

      controlsDiv.appendChild(qtyWrap);
      controlsDiv.appendChild(removeBtn);
      infoDiv.appendChild(controlsDiv);

      li.appendChild(mediaLink);
      li.appendChild(infoDiv);
      ul.appendChild(li);
    });

    bodyEl.appendChild(ul);
    bodyEl.scrollTop = prevScrollTop;
  }

  // ========== 商品バリアント管理 ==========

  async updateProductAvailability() {
    const productCards = document.querySelectorAll('.product-card[data-product-handle]');

    for (const card of productCards) {
      const handle = card.dataset.productHandle;

      if (handle) {
        const product = await this.getProductByHandle(handle);

        if (product) {
          this.updateProductCard(card, product);
        }
      }
    }
  }

  updateProductCard(cardElement, productData) {
    const addToCartBtn = cardElement.querySelector('.add-to-cart-btn');
    if (!addToCartBtn) return;

    const variantId = addToCartBtn.dataset.variantId;
    const variant = this.findVariantById(productData, variantId);

    if (variant) {
      if (variant.availableForSale && !variant.currentlyNotInStock) {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = '🛒 カートに追加';
        addToCartBtn.classList.remove('sold-out');
      } else {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = '売り切れ';
        addToCartBtn.classList.add('sold-out');
      }
    }
  }

  findVariantById(productData, variantId) {
    if (!productData.variants || !productData.variants.edges) return null;

    const variant = productData.variants.edges.find(
      (edge) => edge.node.id === variantId || edge.node.id.endsWith(variantId)
    );

    return variant ? variant.node : null;
  }

  // ========== イベントハンドラー ==========

  bindEvents() {
    document.addEventListener('click', this.handleClick.bind(this));
    document.addEventListener('change', this.handleChange.bind(this));
    document.addEventListener('submit', this.handleSubmit.bind(this));
    document.addEventListener('keydown', this.handleKeydown.bind(this));

    // カスタムイベントリスナー（LP・外部セクション・Warehouse標準セクション共通）
    document.addEventListener('cart:add', this.handleCartAdd.bind(this));
    document.addEventListener('cart:update', this.handleCartUpdate.bind(this));
    document.addEventListener('cart:clear', this.handleCartClear.bind(this));

    // ランキングセクション (ranking-collection.liquid) や旧LPからのカート追加通知
    document.addEventListener('cart:item-added', (e) => {
      this.onExternalCartAdded(e.detail?.button || null);
    });
    document.addEventListener('cart:change', () => {
      this.onExternalCartAdded(null);
    });

    // Warehouse (theme.js) の ProductForm / QuickView / FeaturedCollection からの通知
    document.documentElement.addEventListener('product:added', (e) => {
      if (document.querySelector('.modal[aria-hidden="false"]')) {
        document.dispatchEvent(new CustomEvent('modal:close'));
      }
      this.onExternalCartAdded(e.detail?.button || null);
    });

    document.documentElement.addEventListener('cart:refresh', () => {
      this.updateCartDisplay();
    });

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this.updateCartDisplay();
      }
    });
  }

  async onExternalCartAdded(triggerElement = null) {
    await this.updateCartDisplay();
    this.triggerPostAddFeedback(triggerElement);
  }

  triggerPostAddFeedback(triggerElement = null) {
    const behavior = this.resolveCartBehavior(triggerElement);
    if (behavior === 'page') {
      window.location.href = this.routes.cart_url || '/cart';
    } else if (behavior === 'toast') {
      this.showNotification('商品をカートに追加しました！', 'success', true);
    } else {
      this.openMiniCart();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isMiniCartOpen) {
      this.closeMiniCart();
    }

    if (event.key === 'Enter') {
      const discountInput = event.target?.closest?.('[data-sk-discount-input]');
      if (discountInput) {
        event.preventDefault();
        const code = discountInput.value;
        this.validateAndApplyDiscount(code);
      }
    }
  }

  async handleClick(event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    // クーポンコード関連のクリック処理
    const discountToggle = target.closest('[data-sk-discount-toggle]');
    if (discountToggle) {
      event.preventDefault();
      const container = discountToggle.closest('[data-sk-discount-container]');
      const panel = container?.querySelector('[data-sk-discount-panel]');
      if (panel) {
        const isHidden = panel.hidden;
        panel.hidden = !isHidden;
        discountToggle.setAttribute('aria-expanded', String(!isHidden));
        if (!isHidden) {
          const input = panel.querySelector('[data-sk-discount-input]');
          if (input) setTimeout(() => input.focus(), 50);
        }
      }
      return;
    }

    const discountApplyBtn = target.closest('[data-sk-discount-apply]');
    if (discountApplyBtn) {
      event.preventDefault();
      const container = discountApplyBtn.closest('[data-sk-discount-container]');
      const input = container?.querySelector('[data-sk-discount-input]');
      const code = input?.value || '';
      await this.validateAndApplyDiscount(code);
      return;
    }

    const discountRemoveBtn = target.closest('[data-sk-discount-remove]');
    if (discountRemoveBtn) {
      event.preventDefault();
      this.removeDiscount();
      return;
    }

    // チェックアウトボタンクリック時の割引コード引き継ぎ
    const checkoutBtn = target.closest('button[name="checkout"], .cart-recap__checkout');
    if (checkoutBtn && this.appliedDiscount?.code) {
      const form = checkoutBtn.closest('form');
      if (form) {
        let discountInput = form.querySelector('input[name="discount"]');
        if (!discountInput) {
          discountInput = document.createElement('input');
          discountInput.type = 'hidden';
          discountInput.name = 'discount';
          form.appendChild(discountInput);
        }
        discountInput.value = this.appliedDiscount.code;
      }
    }

    // 1. ミニカート閉じるボタン / オーバーレイクリック
    const closeTrigger = target.closest('[data-sk-mc-close]');
    if (closeTrigger) {
      event.preventDefault();
      this.closeMiniCart();
      return;
    }

    // 2. ミニカート内の数量増減・削除ボタン
    const qtyBtn = target.closest('[data-sk-mc-qty-btn]');
    if (qtyBtn) {
      event.preventDefault();
      const line = parseInt(qtyBtn.dataset.line, 10);
      const quantity = parseInt(qtyBtn.dataset.quantity, 10);
      if (!Number.isNaN(line) && !Number.isNaN(quantity)) {
        const itemRow = qtyBtn.closest('[data-sk-mc-line]');
        if (itemRow) itemRow.classList.add('is-updating');
        try {
          await this.changeCartItem(line, Math.max(0, quantity));
        } catch (error) {
          this.showNotification(error.message, 'error');
          if (itemRow) itemRow.classList.remove('is-updating');
        }
      }
      return;
    }

    // 3. ヘッダーまたはフローティングのカートアイコンクリック
    const miniCartToggle = target.closest(
      '[data-sk-mini-cart-toggle], .cart-link, [data-cart-icon], #cartButton'
    );
    if (miniCartToggle) {
      const behavior = this.resolveCartBehavior(miniCartToggle);
      if (window.theme?.pageType === 'cart' || behavior === 'page') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.toggleMiniCart();
      return;
    }

    // 4. カート追加ボタン (.add-to-cart-btn)
    // ※ ranking-collection.liquid 内のボタンは独自ハンドラーが cart:item-added を発火するため除外
    // ※ おけさ柿LP (okesakaki.js) が読み込まれている場合も二重送信を防ぐため除外
    const addToCartButton = target.closest('.add-to-cart-btn');
    if (
      addToCartButton &&
      addToCartButton.dataset.variantId &&
      !addToCartButton.closest('.ranking-section') &&
      !window.OkesaKakiLP
    ) {
      event.preventDefault();
      await this.handleAddToCartClick(addToCartButton);
      return;
    }

    // 5. チェックアウトボタン
    const checkoutButton = target.closest('.checkout-btn');
    if (checkoutButton) {
      event.preventDefault();
      window.location.href = '/checkout';
    }
  }

  async handleChange(event) {
    const target = event.target;
    if (!target) return;

    // ミニカート内の数量直接入力
    if (target.hasAttribute('data-sk-mc-qty-input')) {
      const line = parseInt(target.dataset.line, 10);
      const quantity = parseInt(target.value, 10);
      if (!Number.isNaN(line) && !Number.isNaN(quantity)) {
        const itemRow = target.closest('[data-sk-mc-line]');
        if (itemRow) itemRow.classList.add('is-updating');
        try {
          await this.changeCartItem(line, Math.max(0, quantity));
        } catch (error) {
          this.showNotification(error.message, 'error');
          if (itemRow) itemRow.classList.remove('is-updating');
        }
      }
      return;
    }

    // 旧クラス互換 (.cart-quantity-input)
    if (target.classList.contains('cart-quantity-input')) {
      const lineItemId = target.dataset.lineItemId;
      const quantity = parseInt(target.value, 10);

      if (lineItemId && !Number.isNaN(quantity)) {
        await this.changeCartItem(lineItemId, Math.max(0, quantity));
      }
    }
  }

  async handleSubmit(event) {
    const form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;

    // Warehouse (theme.js) が管理する標準セクション内のフォームは theme.js 側に任せる
    const isManagedByWarehouse = Boolean(
      form.closest(
        '[data-section-type="product"], [data-section-type="featured-collection"], [data-section-type="collection"], [data-section-type="product-recommendations"], [data-section-type="recently-viewed-products"], [data-section-type="cart"], product-recommendations'
      )
    );
    if (isManagedByWarehouse) return;

    const behavior = this.resolveCartBehavior(form);
    if (behavior === 'page') return;

    event.preventDefault();
    const submitBtn = form.querySelector('[type="submit"], button');
    if (submitBtn) this.setButtonLoading(submitBtn, true);

    try {
      await this.addFormToCart(form);
      this.triggerPostAddFeedback(form);
    } catch (error) {
      this.showNotification(error.message, 'error');
    } finally {
      if (submitBtn) this.setButtonLoading(submitBtn, false);
    }
  }

  async handleAddToCartClick(button) {
    const variantId = button.dataset.variantId;
    const quantity = parseInt(button.dataset.quantity || '1', 10);

    if (!variantId) {
      console.error('バリアントIDが見つかりません');
      return;
    }

    this.setButtonLoading(button, true);

    try {
      await this.addToCart({
        id: variantId,
        quantity: quantity,
      });

      this.triggerPostAddFeedback(button);
    } catch (error) {
      console.error('Add to cart failed:', error);
      this.showNotification(error.message, 'error');
    } finally {
      this.setButtonLoading(button, false);
    }
  }

  // カスタムイベントハンドラー
  async handleCartAdd(event) {
    const { variantId, quantity = 1, properties } = event.detail || {};
    if (!variantId) return;
    await this.addToCart({ id: variantId, quantity, properties });
    this.triggerPostAddFeedback(event.target);
  }

  async handleCartUpdate(event) {
    const { updates } = event.detail || {};
    if (updates) {
      await this.updateCart(updates);
    }
  }

  async handleCartClear() {
    await this.clearCart();
  }

  // ========== ユーティリティメソッド ==========

  setButtonLoading(button, loading) {
    if (loading) {
      if (!button.dataset.originalText) {
        button.dataset.originalText = button.textContent;
      }
      button.dataset.wasDisabled = button.disabled ? 'true' : 'false';
      button.classList.add('loading');
      button.disabled = true;
      button.textContent = '追加中...';
    } else {
      button.classList.remove('loading');
      button.disabled = button.dataset.wasDisabled === 'true';
      button.textContent = button.dataset.originalText || 'カートに追加';
    }
  }

  formatMoney(cents) {
    const numericCents = Number(cents) || 0;
    const amount = numericCents / 100;
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, amount.toFixed(2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(amount).toString())
      .replace(
        /\{\{\s*amount_with_comma_separator\s*\}\}/g,
        amount.toFixed(2).replace('.', ',')
      )
      .replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        Math.round(amount).toLocaleString('ja-JP')
      );
  }

  showNotification(message, type = 'success', showViewCartAction = false) {
    // 既存の通知があれば消去
    document.querySelectorAll('.notification').forEach((n) => n.remove());

    const notification = document.createElement('div');
    notification.className = `notification ${type} show`;

    const icon = document.createElement('span');
    icon.className = 'notification-icon';
    icon.textContent = type === 'success' ? '✓' : '⚠';

    const text = document.createElement('span');
    text.className = 'notification-message';
    text.textContent = message;

    notification.appendChild(icon);
    notification.appendChild(text);

    if (showViewCartAction && type === 'success') {
      const actionBtn = document.createElement('button');
      actionBtn.type = 'button';
      actionBtn.className = 'notification-action';
      actionBtn.textContent = 'カートを見る';
      actionBtn.addEventListener('click', () => {
        notification.remove();
        this.openMiniCart();
      });
      notification.appendChild(actionBtn);
    }

    const closeButton = document.createElement('button');
    closeButton.className = 'notification-close';
    closeButton.setAttribute('aria-label', '閉じる');
    closeButton.type = 'button';
    closeButton.textContent = '×';
    closeButton.addEventListener('click', () => {
      notification.remove();
    });

    notification.appendChild(closeButton);
    document.body.appendChild(notification);

    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 4000);
  }

  dispatchCartEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true,
      cancelable: true,
    });
    document.dispatchEvent(event);
  }

  // ========== 公開API ==========

  async addItemToCart(variantId, quantity = 1, properties = undefined) {
    const result = await this.addToCart({ id: variantId, quantity, properties });
    this.triggerPostAddFeedback();
    return result;
  }

  async removeItemFromCart(lineOrId) {
    return await this.changeCartItem(lineOrId, 0);
  }

  async updateItemQuantity(lineOrId, quantity) {
    return await this.changeCartItem(lineOrId, quantity);
  }

  async getCartData() {
    return await this.getCart();
  }
}

// ========== 初期化 ==========

let shopifyCartAPI = null;

function initializeShopifyCartAPI() {
  if (window.ShopifyCartAPI) return;
  shopifyCartAPI = new ShopifyCartAPI();
  window.ShopifyCartAPI = shopifyCartAPI;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeShopifyCartAPI();
  });
} else {
  initializeShopifyCartAPI();
}